import firebase from '../../firebase/compat';
import { db } from '../../firebase/config';
import { client } from '../../hooks/Client';

/**
 * Everything a household owns. ADR-008: deleting a parent soft-deletes its
 * children, so this list must stay complete — a collection missing here keeps
 * live documents pointing at a deleted household.
 *
 * `PersonaData` keys addresses by `PersonaID`; the rest use `AdressID`.
 */
export const ADRESS_CHILD_COLLECTIONS = [
    { collection: 'Tasks', field: 'AdressID' },
    { collection: 'PersonaData', field: 'PersonaID' },
    { collection: 'AdressNotes', field: 'AdressID' },
    { collection: 'AdressMaterials', field: 'AdressID' },
    { collection: 'ContactMoments', field: 'AdressID' },
];

// Firestore caps a batch at 500 writes.
const BATCH_LIMIT = 500;

/**
 * Soft-delete a household and everything hanging off it (ADR-008).
 *
 * AI-CONTEXT: See docs/decisions.md (ADR-008) and
 * docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch — these are
 * direct reads that bypass the hooks, so every child query carries the tenant
 * filter itself.
 *
 * `DeletedAt` uses serverTimestamp() rather than the module-level `timestamp`:
 * that one is stamped at app load, so a long-open tab would record the wrong
 * moment (docs/pitfalls.md#state-stale-module-level-timestamp).
 *
 * @param {object} params
 * @param {string} params.adressDocid - Firestore document id of the Adresses doc
 * @param {string} params.adressId - The Adresses.ID children point at
 * @param {string} [params.authId]
 * @returns {Promise<{childCount: number}>}
 */
const softDeleteAdress = async ({ adressDocid, adressId, authId }) => {
    if (!adressDocid || !adressId) return { childCount: 0 };

    const deletedFields = {
        Deleted: true,
        DeletedAt: firebase.firestore.FieldValue.serverTimestamp(),
        DeletedBy: authId || '',
    };

    await db.collection('Adresses').doc(adressDocid).update(deletedFields);

    const childRefs = [];

    for (const { collection, field } of ADRESS_CHILD_COLLECTIONS) {
        const snap = await db.collection(collection)
            .where('CompagnyID', '==', client)
            .where(field, '==', adressId)
            .get();

        snap.docs.forEach((doc) => childRefs.push(doc.ref));
    }

    for (let i = 0; i < childRefs.length; i += BATCH_LIMIT) {
        const batch = db.batch();
        childRefs.slice(i, i + BATCH_LIMIT).forEach((ref) => batch.update(ref, deletedFields));
        await batch.commit();
    }

    return { childCount: childRefs.length };
};

export default softDeleteAdress;
